// useObservability composable — ECOUNT ETL 운영 가시성 3 패널 polling 관리
// @MX:ANCHOR: [AUTO] 운영 가시성 데이터 진입점 — SessionPanel/OapiQuotaBadge/BatchHealthPanel 3 컴포넌트 사용
// @MX:REASON: [AUTO] fan_in >= 3 (3 패널 컴포넌트 + Dashboard.vue)
// @MX:SPEC: SPEC-ECOUNT-ETL-OBSERVABILITY-001 AC-FE-001, NR-4
// @MX:WARN: [AUTO] onUnmounted clearInterval 누락 시 메모리 누수 — SPA 라우트 전환 시 누적
// @MX:REASON: [AUTO] setInterval은 GC 대상이 아님. onUnmounted에서 반드시 clearInterval 호출 (NR-4)

import { ref, onMounted, onUnmounted } from 'vue';
import { getSession, getQuota, getHealth, isUnreachable } from '../../../apis/ecountEtlObservability.js';

/**
 * ECOUNT ETL 운영 가시성 30초 polling composable
 * @param {Object} options
 * @param {number} options.intervalMs - polling 간격 (기본 30000ms)
 * @returns {Object} { session, quota, health, loading, errors, fetchAll, lastFetchAt }
 */
export function useObservability(options = {}) {
  const { intervalMs = 30000 } = options;

  // 세션 패널 상태 (SessionStatsDto 8 필드)
  const session = ref(null);
  // OAPI 한도 상태 (OapiQuotaDto 7 필드)
  const quota = ref(null);
  // Batch Health 상태 (BatchHealthDto 6 필드)
  const health = ref(null);
  // 패널별 로딩 상태 (독립 병렬 fetch)
  const loading = ref({ session: false, quota: false, health: false });
  // 패널별 에러 상태 (batch unreachable 포함)
  const errors = ref({ session: null, quota: null, health: null });
  // 마지막 fetch 시각
  const lastFetchAt = ref(null);

  let intervalId = null;

  /**
   * 세션 상태 단독 fetch
   * unreachable 응답은 errors.session에 기록, session은 null 유지
   */
  async function fetchSession() {
    loading.value.session = true;
    errors.value.session = null;
    try {
      const res = await getSession();
      if (isUnreachable(res.data)) {
        errors.value.session = { type: 'unreachable', reason: res.data.reason };
      } else {
        session.value = res.data;
      }
    } catch (e) {
      errors.value.session = { type: 'error', message: e?.response?.data?.message || e.message };
    } finally {
      loading.value.session = false;
    }
  }

  /**
   * OAPI 한도 단독 fetch
   */
  async function fetchQuota() {
    loading.value.quota = true;
    errors.value.quota = null;
    try {
      const res = await getQuota();
      if (isUnreachable(res.data)) {
        errors.value.quota = { type: 'unreachable', reason: res.data.reason };
      } else {
        quota.value = res.data;
      }
    } catch (e) {
      errors.value.quota = { type: 'error', message: e?.response?.data?.message || e.message };
    } finally {
      loading.value.quota = false;
    }
  }

  /**
   * Batch Health 단독 fetch
   */
  async function fetchHealth() {
    loading.value.health = true;
    errors.value.health = null;
    try {
      const res = await getHealth();
      if (isUnreachable(res.data)) {
        errors.value.health = { type: 'unreachable', reason: res.data.reason };
      } else {
        health.value = res.data;
      }
    } catch (e) {
      errors.value.health = { type: 'error', message: e?.response?.data?.message || e.message };
    } finally {
      loading.value.health = false;
    }
  }

  /**
   * 3 패널 병렬 fetch — Promise.all로 동시 호출
   * 패널별 독립 에러 처리 (1 패널 unreachable이어도 나머지 정상 표시)
   */
  async function fetchAll() {
    await Promise.all([fetchSession(), fetchQuota(), fetchHealth()]);
    lastFetchAt.value = new Date();
  }

  // onMounted: 최초 fetch + 30초 polling 시작
  onMounted(() => {
    fetchAll();
    intervalId = setInterval(() => {
      // 탭 숨김 시 polling 스킵 (배터리/리소스 절약)
      if (document.visibilityState === 'hidden') return;
      fetchAll();
    }, intervalMs);
  });

  // onUnmounted: setInterval 반드시 해제 (NR-4 — 메모리 누수 방지)
  onUnmounted(() => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });

  return {
    session,
    quota,
    health,
    loading,
    errors,
    fetchAll,
    fetchSession,
    lastFetchAt,
  };
}
